import { createRouter, createWebHistory } from "vue-router"
import config from "config"
import { pollyEntryGuard } from "@/polly/services/polly-entry-guard.js"
import { isSessionExpired, isGuestSession, clearSession, setPasskeySessionExpiredHandler } from "@/polly/services/polly-session.js"

/**
 * The routes of the Polly app. All of them are public: a polly has no login screen. Identity comes from a passkey,
 * and only when the visitor actually creates or votes - see src/polly/services/polly-passkey.js. So unlike LIQUIDO's router
 * there is no login guard; the one decision at the entry is pollyEntryGuard, the beforeEnter of /polly. The one global
 * guard below only drops a session that has run out (14 days without a visit, or 90 days after the passkey tap) and sends a passkey user to /polly/login.
 */

/** The step of writing a new polly (?step=write | about | live) is a prop of polly-page.vue, so that its Back button goes a step back */
const stepOf = route => (typeof route.query.step === "string" ? route.query.step : undefined)

const routes = [
	{
		// polly.liquido.vote itself: the entry of the flow
		path: "/",
		redirect: "/polly",
	},
	{
		// The entry to polly. Somebody signed in with a passkey is sent on to /pollys - that is decided by
		// pollyEntryGuard, because only the polly code may know the polly session.
		path: "/polly",
		name: "createPolly",
		component: () => import("@/polly/views/polly-page.vue"),
		props: route => ({ step: stepOf(route) }),
		beforeEnter: pollyEntryGuard,
	},
	{
		// "My Pollys": every polly this device's passkey created. Without a polly session the page itself asks for
		// the passkey, there is no login screen to redirect to.
		path: "/pollys",
		name: "pollys",
		component: () => import("@/polly/views/pollys-list.vue"),
	},
	{
		// The passkey session has run out (14 days without a visit, 90 after the passkey tap): log in again with the passkey, then on to where the user
		// was going (?redirect=). Reached only through the guard below and the polly client - never linked. Declared
		// BEFORE /polly/:publicId, or "login" would be taken for a public id.
		path: "/polly/login",
		name: "pollyLogin",
		component: () => import("@/polly/views/polly-login.vue"),
		props: route => ({ redirect: typeof route.query.redirect === "string" ? route.query.redirect : undefined }),
	},
	{
		// The link in a polly's link mail that confirms its creator's email: their passkey then belongs to
		// the account with that address. Opened with the passkey the polly was started with, see
		// polly-confirm-email.vue. Declared BEFORE /polly/:publicId, or "confirm" would be taken for a public id.
		path: "/polly/confirm",
		name: "confirmPollyEmail",
		component: () => import("@/polly/views/polly-confirm-email.vue"),
	},
	{
		// The ONE link the creator shares. There is no separate admin link and no secret in
		// the URL - whoever opens it is recognised by their own passkey, so the creator gets
		// the admin buttons and everybody else gets a ballot.
		path: "/polly/:publicId",
		name: "showPolly",
		component: () => import("@/polly/views/polly-page.vue"),
		props: route => ({ publicId: route.params.publicId, step: stepOf(route) }),
	},
	// The legal pages are LIQUIDO's and Polly's, one text for both (src/shared/views)
	{ path: "/impressum", name: "impressum", component: () => import("@/shared/views/impressum.vue") },
	{ path: "/agb", name: "agb", component: () => import("@/shared/views/agb.vue") },
	{ path: "/datenschutz", name: "datenschutz", component: () => import("@/shared/views/datenschutz.vue") },
]

if (import.meta.env.MODE === "development") {
	routes.push({
		// Development only: every Polly screen on one page, the fastest way to review them
		path: "/_design-overview",
		component: () => import("@/polly/views/_design-overview.vue"),
	})
	routes.push({
		// Development only: the owl, one mood at a time (a button takes her through all of them), to review her animations
		path: "/_owl-moods",
		component: () => import("@/polly/views/_owl-moods.vue"),
	})
}

// Anything else is a 404, as in LIQUIDO. An unknown polly id is not: /polly/:publicId is a route, and polly-page.vue says that it cannot find that polly.
routes.push({ path: "/404", name: "pageNotFound", component: () => import("@/polly/views/not-found-page.vue") })
routes.push({ path: "/:pathMatch(.*)*", redirect: "/404" })

const router = createRouter({
	history: createWebHistory(config.BASE_URL),
	// Scroll position is managed by the pages themselves ($root.scrollToTop), as in LIQUIDO's router
	scrollBehavior: () => false,
	routes,
})

/**
 * An expired polly session must not be sent: the backend refuses every request that carries it with HTTP 401, even
 * reading a polly. So it is dropped here, before the page asks anything. A passkey user is sent to the login page,
 * and comes back to where they were going; a guest just goes on - the next vote gets a fresh guest session.
 * (The polly client does the same for a session that runs out while a page is open, see polly-session.js.)
 */
router.beforeEach(to => {
	if (to.name === "pollyLogin" || !isSessionExpired()) return
	const wasPasskey = !isGuestSession()
	clearSession()
	if (wasPasskey) return { name: "pollyLogin", query: { redirect: to.fullPath } }
})

/** The polly client found the passkey session refused or run out while a page was open: log in again, then back here. */
setPasskeySessionExpiredHandler(() => {
	const current = router.currentRoute.value
	if (current.name === "pollyLogin") return
	// The address bar, not currentRoute: on the very first request of a page load the router has not arrived yet
	const base = (config.BASE_URL || "/").replace(/\/$/, "")
	const path = window.location.pathname + window.location.search
	const redirect = base && path.startsWith(base) ? path.slice(base.length) || "/" : path
	router.push({ name: "pollyLogin", query: { redirect } })
})

export default router
